/**
 * Channel Manager — page for managing messaging platform integrations.
 * Lists channels, allows creating new webhook/Slack channels, test connection.
 */

import { useState, useEffect, useCallback } from "react";
import type { Channel, ChannelType, Agent } from "../api/client.js";
import {
  listChannels,
  createChannel,
  updateChannel,
  deleteChannel,
  testChannel,
  listAgents,
} from "../api/client.js";

const CHANNEL_TYPE_META: Record<ChannelType, { label: string; description: string }> = {
  webhook: { label: "Webhook", description: "Receive HTTP POST messages" },
  slack: { label: "Slack", description: "Slack Bot integration" },
  telegram: { label: "Telegram", description: "Telegram Bot API integration" },
  discord: { label: "Discord", description: "Discord Bot with slash commands" },
  whatsapp: { label: "WhatsApp", description: "WhatsApp Business Cloud API" },
  teams: { label: "Microsoft Teams", description: "Teams Bot Framework integration" },
  google_chat: { label: "Google Chat", description: "Google Chat webhook and bot" },
  signal: { label: "Signal", description: "Signal messenger via signal-cli" },
};

export function ChannelManager() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [chanRes, agentRes] = await Promise.all([
        listChannels(),
        listAgents(),
      ]);
      setChannels(chanRes.channels);
      setAgents(agentRes.agents);
    } catch {
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async (ch: Channel) => {
    await updateChannel(ch.id, { enabled: !ch.enabled });
    fetchData();
  };

  const handleDelete = async (id: string) => {
    await deleteChannel(id);
    fetchData();
  };

  const handleTest = async (id: string) => {
    try {
      const result = await testChannel(id);
      setTestResults((prev) => ({ ...prev, [id]: result }));
    } catch {
      setTestResults((prev) => ({ ...prev, [id]: { success: false, message: "Test failed" } }));
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-950">Channels</h1>
          <p className="mt-1 text-sm text-ink-500">
            Manage messaging platform integrations for your agents.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
        >
          Register Channel
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-300 border-t-accent-600" />
        </div>
      ) : channels.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ink-200 p-12 text-center">
          <p className="text-ink-400">No channels registered yet.</p>
          <p className="mt-1 text-sm text-ink-300">
            Register a webhook or Slack channel to let your agents receive messages.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {channels.map((ch) => {
            const meta = CHANNEL_TYPE_META[ch.type as ChannelType] ?? { label: ch.type, description: "" };
            const agentName = agents.find((a) => a.id === ch.agentId)?.name ?? ch.agentId;
            const test = testResults[ch.id];

            return (
              <div key={ch.id} className="rounded-xl border border-ink-100 bg-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-ink-900">{ch.name}</span>
                      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-medium text-ink-600">
                        {meta.label}
                      </span>
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                        ch.enabled ? "bg-green-100 text-green-700" : "bg-ink-100 text-ink-400"
                      }`}>
                        {ch.enabled ? "Active" : "Disabled"}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-ink-400">
                      Agent: {agentName} &middot; Created: {new Date(ch.createdAt).toLocaleDateString()}
                    </p>
                    {test && (
                      <p className={`mt-1 text-xs ${test.success ? "text-green-600" : "text-red-600"}`}>
                        Test: {test.message}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleTest(ch.id)}
                      className="rounded px-2 py-1 text-xs text-accent-600 hover:bg-accent-50"
                    >
                      Test
                    </button>
                    <button
                      onClick={() => handleToggle(ch)}
                      className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-surface-2"
                    >
                      {ch.enabled ? "Disable" : "Enable"}
                    </button>
                    <button
                      onClick={() => handleDelete(ch.id)}
                      className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddChannelModal
          agents={agents}
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchData(); }}
        />
      )}
    </div>
  );
}

// ── Add Channel Modal ─────────────────────────────────────────────────────

function AddChannelModal({
  agents,
  onClose,
  onCreated,
}: {
  agents: Agent[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [type, setType] = useState<ChannelType>("webhook");
  const [name, setName] = useState("");
  const [agentId, setAgentId] = useState(agents[0]?.id ?? "");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [slackSigningSecret, setSlackSigningSecret] = useState("");
  const [slackBotToken, setSlackBotToken] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const config: Record<string, unknown> = {};
      if (type === "webhook") {
        config.secret = webhookSecret;
      } else if (type === "slack") {
        config.signingSecret = slackSigningSecret;
        config.botToken = slackBotToken;
      }
      await createChannel({ type, name, agentId, config });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create channel");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-bold text-ink-950">Register Channel</h2>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Channel Type</label>
            <select
              value={type} onChange={(e) => setType(e.target.value as ChannelType)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            >
              {(Object.keys(CHANNEL_TYPE_META) as ChannelType[]).map((t) => (
                <option key={t} value={t}>
                  {CHANNEL_TYPE_META[t].label} — {CHANNEL_TYPE_META[t].description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Name</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="My Webhook" className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Agent</label>
            <select
              value={agentId} onChange={(e) => setAgentId(e.target.value)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            >
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          {type === "webhook" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">HMAC Secret</label>
              <input
                type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="Shared secret for signature verification"
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
              />
            </div>
          )}

          {type === "slack" && (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-600">Signing Secret</label>
                <input
                  type="password" value={slackSigningSecret} onChange={(e) => setSlackSigningSecret(e.target.value)}
                  placeholder="Slack app signing secret"
                  className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-600">Bot Token</label>
                <input
                  type="password" value={slackBotToken} onChange={(e) => setSlackBotToken(e.target.value)}
                  placeholder="xoxb-..."
                  className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
                />
              </div>
            </>
          )}
        </div>

        {error && <div className="mt-3 rounded-lg bg-red-50 p-2 text-xs text-red-600">{error}</div>}

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-lg border border-ink-200 py-2 text-sm font-medium text-ink-600 hover:bg-surface-2">
            Cancel
          </button>
          <button
            onClick={handleCreate} disabled={creating || !name || !agentId}
            className="flex-1 rounded-lg bg-accent-600 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {creating ? "Creating..." : "Register"}
          </button>
        </div>
      </div>
    </div>
  );
}
